import { useMemo, useState } from 'react';
import { Alert, Button, Input, InputNumber, Modal, Select, Table, Tabs, Tag, Tooltip, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import { FilePlus2, History, Plus, Printer, ReceiptText, ScrollText, Trash2, X } from 'lucide-react';
import Layout from '../../Layout';
import { printHtml } from '../Treatment/print';
import { ktApi, today, useKt, type DraftInput } from './ktStore';
import {
  auditRows, billHtml, draftHtml, draftPayErrors, draftSaveErrors, draftTotal, sellable, SURCHARGE, type SurchargeType,
} from './ktCounter';
import { billById, billTotal, COUNTER_USERS, dmy, fmtMoney, needsRef, PAY_METHOD, REF_LABEL, type Bill, type BillDraft, type DraftLine, type PayMethod, type Payment } from './ktLogic';
import { CounterBar, DemoBar, Kpi, PageHeader, Panel, showErrors } from './ktUi';

const money = (v: number | string | undefined) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
const unmoney = (v: string | undefined) => Number((v || '').replace(/\./g, ''));
const uid = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
const CASH_METHODS: PayMethod[] = ['cash', 'transfer', 'vietqr', 'payoo', 'installment'];
const newPayment = (method: PayMethod = 'cash', amount = 0): Payment => ({ id: uid(), method, amount, ref: '' });

/** Thu đa hình thức trên một bill */
export function PaymentEditor({ payments, onChange, total, idPrefix = 'kt-pay' }: { payments: Payment[]; onChange: (p: Payment[]) => void; total: number; idPrefix?: string }) {
  const up = (id: string, patch: Partial<Payment>) => onChange(payments.map(p => (p.id === id ? { ...p, ...patch } : p)));
  const paid = payments.reduce((t, p) => t + p.amount, 0);
  const left = total - paid;
  return (
    <div className="space-y-2">
      {payments.map((p, i) => (
        <div key={p.id} className="grid grid-cols-1 sm:grid-cols-[150px_140px_1fr_32px] gap-2 items-end">
          <div>
            <label htmlFor={`${idPrefix}-m-${i}`} className="text-xs font-medium">Hình thức</label>
            <Select id={`${idPrefix}-m-${i}`} className="w-full" value={p.method} onChange={v => up(p.id, { method: v, ref: '' })}
              options={CASH_METHODS.map(m => ({ value: m, label: PAY_METHOD[m] }))} />
          </div>
          <div>
            <label htmlFor={`${idPrefix}-a-${i}`} className="text-xs font-medium">Số tiền</label>
            <InputNumber id={`${idPrefix}-a-${i}`} className="!w-full" min={0} step={100000} value={p.amount} onChange={v => up(p.id, { amount: Number(v) || 0 })} formatter={money} parser={unmoney} />
          </div>
          <div>
            <label htmlFor={`${idPrefix}-r-${i}`} className="text-xs font-medium">{REF_LABEL[p.method]}</label>
            <Input id={`${idPrefix}-r-${i}`} disabled={!needsRef(p.method)} value={p.ref} onChange={e => up(p.id, { ref: e.target.value })} placeholder={needsRef(p.method) ? 'Bắt buộc' : 'Không cần'} />
          </div>
          <Button type="text" danger icon={<X size={14} />} aria-label={`Bỏ dòng thu ${i + 1}`} onClick={() => onChange(payments.filter(x => x.id !== p.id))} />
        </div>
      ))}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button size="small" icon={<Plus size={13} />} onClick={() => onChange([...payments, newPayment('cash', Math.max(0, left))])}>Thêm hình thức thu</Button>
        <span className={`text-xs ${left ? 'text-rose-600 font-medium' : 'text-emerald-700'}`}>Đã nhập {fmtMoney(paid)} / {fmtMoney(total)}{left ? ` · ${left > 0 ? 'còn thiếu' : 'thừa'} ${fmtMoney(Math.abs(left))}` : ' · đúng tiền nét'}</span>
      </div>
    </div>
  );
}

/** Thu tiền tại quầy: tạm tính trước, thu chính thức mới cấp số bill (SRS 3.1, 3.2) */
export default function KeToanCounter() {
  const s = useKt();
  const now = today();
  const [editing, setEditing] = useState<BillDraft | 'new' | null>(null);
  const [dropping, setDropping] = useState<BillDraft | null>(null);
  const [reason, setReason] = useState('');
  const [surcharge, setSurcharge] = useState(false);

  const drafts = s.drafts.filter(d => d.status === 'draft');
  const done = s.drafts.filter(d => d.status !== 'draft');
  const surcharges = useMemo(() => s.bills.filter(b => b.kind === 'surcharge').sort((a, b) => b.seq - a.seq), [s.bills]);
  const audit = useMemo(() => auditRows(s), [s]);
  const counter = s.tier === 'counter';

  const draftColumns: ColumnsType<BillDraft> = [
    { title: 'Số tạm tính', dataIndex: 'no', width: 110 },
    { title: 'Giờ', key: 'at', width: 70, render: (_, d) => dayjs(d.updatedAt).format('HH:mm') },
    { title: 'Khách, dịch vụ', key: 'c', render: (_, d) => <div><div className="text-gray-900 dark:text-gray-100">{d.customer}</div><div className="text-[11px] text-gray-500">{d.lines.map(l => l.name).join(', ')}</div></div> },
    { title: 'Tiền nét', key: 'm', width: 130, align: 'right', render: (_, d) => <b>{fmtMoney(draftTotal(d))}</b> },
    { title: 'Người lập', dataIndex: 'by', width: 180 },
    { title: '', key: 'a', width: 190, align: 'right', render: (_, d) => (
      <div className="flex justify-end gap-1">
        {counter && <Button size="small" type="primary" onClick={() => setEditing(d)}>Mở, thu tiền</Button>}
        <Tooltip title="In tạm tính"><Button size="small" type="text" icon={<Printer size={14} />} aria-label={`In ${d.no}`} onClick={() => printHtml(`Tạm tính ${d.no}`, 'A4', draftHtml(d))} /></Tooltip>
        {counter && <Tooltip title="Khách đổi ý: bỏ tạm tính, không phát sinh bill hủy"><Button size="small" type="text" danger icon={<Trash2 size={14} />} aria-label={`Bỏ ${d.no}`} onClick={() => { setReason(''); setDropping(d); }} /></Tooltip>}
      </div>
    ) },
  ];
  const doneColumns: ColumnsType<BillDraft> = [
    { title: 'Số tạm tính', dataIndex: 'no', width: 110 },
    { title: 'Khách', dataIndex: 'customer' },
    { title: 'Tiền nét', key: 'm', width: 130, align: 'right', render: (_, d) => fmtMoney(draftTotal(d)) },
    { title: 'Kết quả', key: 's', render: (_, d) => {
      const b = billById(s, d.billId);
      return d.status === 'paid' ? <Tag color="green">Đã thu, {b?.no}</Tag> : <span><Tag>Đã bỏ</Tag><span className="text-xs text-gray-500">{d.reason}</span></span>;
    } },
    { title: '', key: 'a', width: 70, render: (_, d) => {
      const b = billById(s, d.billId);
      return b ? <Tooltip title="In bill"><Button size="small" type="text" icon={<Printer size={14} />} aria-label={`In bill ${b.no}`} onClick={() => printHtml(`Bill ${b.no}`, 'A4', billHtml(b))} /></Tooltip> : null;
    } },
  ];
  const surchargeColumns: ColumnsType<Bill> = [
    { title: 'Số phiếu', dataIndex: 'no', width: 110, render: (no: string, b) => <span className={b.status === 'cancelled' ? 'line-through text-gray-400' : ''}>{no}</span> },
    { title: 'Ngày', key: 'd', width: 100, render: (_, b) => dmy(b.date) },
    { title: 'Loại', key: 't', render: (_, b) => b.lines[0]?.name },
    { title: 'Khách', dataIndex: 'customer' },
    { title: 'Bill chính', key: 'p', width: 110, render: (_, b) => billById(s, b.parentBillId)?.no || <span className="text-gray-400">Không gắn</span> },
    { title: 'Số tiền', key: 'm', width: 120, align: 'right', render: (_, b) => <b>{fmtMoney(billTotal(b))}</b> },
    { title: 'Thu bằng', key: 'x', width: 180, render: (_, b) => b.payments.map(p => <div key={p.id} className="text-xs">{PAY_METHOD[p.method]}{p.ref ? ` · ${p.ref}` : ''}</div>) },
  ];
  type Audit = (typeof audit)[number];
  const auditColumns: ColumnsType<Audit> = [
    { title: 'Thời gian', key: 'at', width: 130, render: (_, r) => dayjs(r.at).format('HH:mm DD/MM/YYYY') },
    { title: 'Bill', dataIndex: 'billNo', width: 100 },
    { title: 'Thao tác', dataIndex: 'action', width: 90 },
    { title: 'Người thực hiện', dataIndex: 'by', width: 190 },
    { title: 'Lý do', dataIndex: 'reason' },
    { title: 'Giá trị cũ, mới', key: 'c', render: (_, r) => r.changes?.map(c => <div key={c.field} className="text-xs"><span className="text-gray-500">{c.field}:</span> {c.from} → <b>{c.to}</b></div>) },
  ];

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Thu tiền tại quầy" desc="Lưu tạm tính để khách xem trước, thu chính thức mới cấp số bill."
          extra={counter ? <Button type="primary" icon={<FilePlus2 size={14} />} disabled={!!s.locks[now]} onClick={() => setEditing('new')}>Lập tạm tính</Button> : undefined} />
        <DemoBar portal="counter" />
        <CounterBar />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Kpi icon={<ReceiptText size={20} />} tone="bg-sky-50 text-sky-600" label="Tạm tính đang chờ" value={drafts.length} hint="Chưa cấp số bill" />
          <Kpi icon={<ScrollText size={20} />} tone="bg-amber-50 text-amber-600" label="Phiếu phụ thu hôm nay" value={surcharges.filter(b => b.date === now && b.status === 'valid').length} hint={fmtMoney(surcharges.filter(b => b.date === now && b.status === 'valid').reduce((t, b) => t + billTotal(b), 0))} />
          <Kpi icon={<History size={20} />} tone="bg-rose-50 text-rose-600" label="Lượt sửa, hủy hôm nay" value={audit.filter(a => a.date === now).length} hint="Xem ở tab Nhật ký" />
        </div>

        <Panel title="Chứng từ tại quầy">
          <Tabs className="px-4" items={[
            { key: 'drafts', label: `Tạm tính (${drafts.length})`, children: <div className="pb-4"><Table rowKey="id" size="middle" columns={draftColumns} dataSource={drafts} pagination={false} scroll={{ x: 850 }} locale={{ emptyText: 'Không có tạm tính đang chờ' }} /></div> },
            { key: 'done', label: `Đã xử lý (${done.length})`, children: <div className="pb-4"><Table rowKey="id" size="middle" columns={doneColumns} dataSource={done} pagination={{ pageSize: 15, hideOnSinglePage: true }} scroll={{ x: 700 }} locale={{ emptyText: 'Chưa có tạm tính nào được thu hoặc bỏ' }} /></div> },
            { key: 'surcharge', label: `Phiếu phụ thu (${surcharges.length})`, children: (
              <div className="pb-4 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs text-gray-500 m-0">Phiếu riêng, số chứng từ cùng chuỗi bill, sửa hoặc hủy ở màn <Link to="/ke-toan/counter-bills" className="text-[#3e91ab] hover:underline">Bill trong ngày</Link> và không đụng tới bill chính.</p>
                  {counter && <Button icon={<Plus size={14} />} disabled={!!s.locks[now]} onClick={() => setSurcharge(true)}>Lập phiếu phụ thu</Button>}
                </div>
                <Table rowKey="id" size="middle" columns={surchargeColumns} dataSource={surcharges} pagination={{ pageSize: 15, hideOnSinglePage: true }} scroll={{ x: 900 }} locale={{ emptyText: 'Chưa có phiếu phụ thu' }} />
              </div>
            ) },
            { key: 'audit', label: `Nhật ký sửa, hủy (${audit.length})`, children: <div className="pb-4"><Table rowKey="key" size="middle" columns={auditColumns} dataSource={audit} pagination={{ pageSize: 20, hideOnSinglePage: true }} scroll={{ x: 1000 }} locale={{ emptyText: 'Chưa có lượt sửa, hủy' }} /></div> },
          ]} />
        </Panel>
      </div>

      {editing && <DraftModal draft={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
      {surcharge && <SurchargeModal onClose={() => setSurcharge(false)} />}

      <Modal open={!!dropping} title={`Bỏ tạm tính ${dropping?.no}`} okText="Bỏ tạm tính" okButtonProps={{ danger: true }} cancelText="Quay lại" onCancel={() => setDropping(null)} destroyOnHidden
        onOk={() => { const r = ktApi.dropDraft(dropping!.id, reason); if (r.ok) { message.success('Đã bỏ, không phát sinh bill hủy'); setDropping(null); } else showErrors(r.errors); }}>
        <label htmlFor="kt-drop-reason" className="text-sm font-medium">Lý do</label>
        <Input.TextArea id="kt-drop-reason" rows={2} value={reason} onChange={e => setReason(e.target.value)} className="!mt-1" />
      </Modal>
    </Layout>
  );
}

function DraftModal({ draft, onClose }: { draft: BillDraft | null; onClose: () => void }) {
  const s = useKt();
  const [f, setF] = useState<DraftInput>(draft
    ? { id: draft.id, customer: draft.customer, phone: draft.phone, kind: draft.kind, sale: draft.sale, lines: draft.lines, orderDiscount: draft.orderDiscount, payments: draft.payments, note: draft.note }
    : { customer: '', phone: '', kind: 'service', sale: '', lines: [], orderDiscount: 0, payments: [], note: '' });
  const up = (p: Partial<DraftInput>) => setF(x => ({ ...x, ...p }));
  const setLine = (id: string, p: Partial<DraftLine>) => up({ lines: f.lines.map(l => (l.id === id ? { ...l, ...p } : l)) });
  const options = sellable(s.services).map(x => ({ value: x.id, label: `${x.code} · ${x.name}` }));
  const addLine = (serviceId: string) => {
    const sv = s.services.find(x => x.id === serviceId);
    if (sv) up({ lines: [...f.lines, { id: uid(), serviceId, name: sv.name, list: sv.price, lineDiscount: 0 }] });
  };
  const total = draftTotal(f);
  const payErrors = f.lines.length ? draftPayErrors(s, { ...f, id: f.id || 'new', no: '', at: 0, updatedAt: 0, by: '', status: 'draft' }) : [];
  const save = () => {
    const r = ktApi.saveDraft(f);
    if (r.ok) { message.success('Đã lưu tạm tính, chưa cấp số bill'); onClose(); } else showErrors(r.errors);
  };
  const pay = () => {
    const saved = ktApi.saveDraft(f);
    if (!saved.ok) { showErrors(saved.errors); return; }
    const r = ktApi.payDraft(saved.id!);
    if (r.ok) { message.success(`Đã xuất ${billById(ktApi.snapshot(), r.id)?.no}`); onClose(); } else showErrors(r.errors);
  };
  return (
    <Modal open width={860} title={draft ? `Tạm tính ${draft.no}` : 'Lập tạm tính'} onCancel={onClose} destroyOnHidden
      footer={[
        <Button key="c" onClick={onClose}>Thôi</Button>,
        <Button key="s" disabled={draftSaveErrors(f).length > 0} onClick={save}>Lưu tạm tính</Button>,
        <Button key="p" type="primary" disabled={payErrors.length > 0 || !f.lines.length} onClick={pay}>Thu tiền, xuất bill</Button>,
      ]}>
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div className="sm:col-span-2"><label htmlFor="kt-dr-customer" className="text-sm font-medium">Khách hàng</label><Input id="kt-dr-customer" value={f.customer} onChange={e => up({ customer: e.target.value })} /></div>
          <div><label htmlFor="kt-dr-phone" className="text-sm font-medium">Số điện thoại</label><Input id="kt-dr-phone" value={f.phone} onChange={e => up({ phone: e.target.value })} /></div>
          <div><label htmlFor="kt-dr-kind" className="text-sm font-medium">Loại bill</label><Select id="kt-dr-kind" className="w-full" value={f.kind} onChange={v => up({ kind: v })} options={[{ value: 'service', label: 'Dịch vụ, sản phẩm' }, { value: 'deposit', label: 'Đặt cọc' }]} /></div>
        </div>

        <div>
          <label htmlFor="kt-dr-add" className="text-sm font-medium">Thêm dịch vụ</label>
          <Select id="kt-dr-add" className="w-full" showSearch optionFilterProp="label" placeholder="Tìm theo mã hoặc tên" value={null} onChange={v => v && addLine(v)} options={options} />
          <p className="text-[11px] text-gray-500 m-0 mt-1">Gói liệu trình bán trên POS để hệ thống sinh thẻ.</p>
        </div>
        {f.lines.length > 0 && (
          <div className="space-y-2">
            {f.lines.map((l, i) => (
              <div key={l.id} className="grid grid-cols-1 sm:grid-cols-[1fr_130px_130px_32px] gap-2 items-end">
                <div className="text-sm pb-1">{l.name}</div>
                <div><label htmlFor={`kt-dr-list-${i}`} className="text-xs font-medium">Giá</label><InputNumber id={`kt-dr-list-${i}`} className="!w-full" min={0} step={100000} value={l.list} onChange={v => setLine(l.id, { list: Number(v) || 0 })} formatter={money} parser={unmoney} /></div>
                <div><label htmlFor={`kt-dr-ld-${i}`} className="text-xs font-medium">Chiết khấu dòng</label><InputNumber id={`kt-dr-ld-${i}`} className="!w-full" min={0} step={100000} value={l.lineDiscount} onChange={v => setLine(l.id, { lineDiscount: Number(v) || 0 })} formatter={money} parser={unmoney} /></div>
                <Button type="text" danger icon={<X size={14} />} aria-label={`Bỏ ${l.name}`} onClick={() => up({ lines: f.lines.filter(x => x.id !== l.id) })} />
              </div>
            ))}
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div><label htmlFor="kt-dr-od" className="text-sm font-medium">Chiết khấu tổng đơn</label><InputNumber id="kt-dr-od" className="!w-full" min={0} step={100000} value={f.orderDiscount} onChange={v => up({ orderDiscount: Number(v) || 0 })} formatter={money} parser={unmoney} /></div>
          <div>
            <label htmlFor="kt-dr-sale" className="text-sm font-medium">Sale</label>
            <Select id="kt-dr-sale" className="w-full" allowClear placeholder="Để tính hoa hồng" value={f.sale || undefined} onChange={v => up({ sale: v || '' })} options={COUNTER_USERS.filter(u => u.role === 'sale_offline').map(u => ({ value: u.name, label: u.name }))} />
          </div>
          <div className="flex flex-col justify-end text-right"><span className="text-xs text-gray-500">Tiền nét phải thu</span><span className="text-lg font-semibold">{fmtMoney(total)}</span></div>
        </div>

        <div>
          <div className="text-sm font-medium mb-1">Hình thức thu</div>
          <PaymentEditor payments={f.payments} onChange={payments => up({ payments })} total={total} idPrefix="kt-dr-pay" />
        </div>
        {payErrors.length > 0 && f.payments.length > 0 && <Alert type="warning" showIcon message="Chưa thu chính thức được" description={<ul className="m-0 pl-4">{payErrors.map(e => <li key={e}>{e}</li>)}</ul>} />}
      </div>
    </Modal>
  );
}

function SurchargeModal({ onClose }: { onClose: () => void }) {
  const s = useKt();
  const now = today();
  const [type, setType] = useState<SurchargeType>('pain');
  const [parentBillId, setParent] = useState<string | undefined>();
  const [customer, setCustomer] = useState('');
  const [phone, setPhone] = useState('');
  const [amount, setAmount] = useState(0);
  const [payments, setPayments] = useState<Payment[]>([newPayment('cash')]);
  const [note, setNote] = useState('');
  const parents = s.bills.filter(b => b.date === now && b.status === 'valid' && (b.kind === 'service' || b.kind === 'card')).sort((a, b) => b.seq - a.seq);
  const pickParent = (id?: string) => {
    setParent(id);
    const b = s.bills.find(x => x.id === id);
    if (b) { setCustomer(b.customer); setPhone(b.phone); }
  };
  return (
    <Modal open width={720} title="Lập phiếu phụ thu" okText="Thu tiền, xuất phiếu" cancelText="Thôi" onCancel={onClose} destroyOnHidden
      onOk={() => { const r = ktApi.createSurcharge({ type, customer, phone, parentBillId, amount, payments, note }); if (r.ok) { message.success('Đã lập phiếu phụ thu'); onClose(); } else showErrors(r.errors); }}>
      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label htmlFor="kt-sc-type" className="text-sm font-medium">Loại phụ thu</label><Select id="kt-sc-type" className="w-full" value={type} onChange={setType} options={(Object.keys(SURCHARGE) as SurchargeType[]).map(k => ({ value: k, label: SURCHARGE[k].label }))} /></div>
          <div><label htmlFor="kt-sc-parent" className="text-sm font-medium">Bill chính (không bắt buộc)</label><Select id="kt-sc-parent" className="w-full" allowClear showSearch optionFilterProp="label" placeholder="Chọn bill hôm nay" value={parentBillId} onChange={pickParent} options={parents.map(b => ({ value: b.id, label: `${b.no} · ${b.customer}` }))} /></div>
          <div><label htmlFor="kt-sc-customer" className="text-sm font-medium">Khách hàng</label><Input id="kt-sc-customer" value={customer} onChange={e => setCustomer(e.target.value)} /></div>
          <div><label htmlFor="kt-sc-amount" className="text-sm font-medium">Số tiền</label><InputNumber id="kt-sc-amount" className="!w-full" min={0} step={50000} value={amount} onChange={v => { const n = Number(v) || 0; setAmount(n); if (payments.length === 1) setPayments([{ ...payments[0], amount: n }]); }} formatter={money} parser={unmoney} /></div>
        </div>
        {type === 'vat' && <Alert type="warning" showIcon message="Giá bán đang gồm VAT. Thu thêm phí VAT có thể tính thuế hai lần, chờ Kế toán trưởng chốt cách ghi." />}
        <PaymentEditor payments={payments} onChange={setPayments} total={amount} idPrefix="kt-sc-pay" />
        <div><label htmlFor="kt-sc-note" className="text-sm font-medium">Ghi chú</label><Input id="kt-sc-note" value={note} onChange={e => setNote(e.target.value)} /></div>
      </div>
    </Modal>
  );
}
